import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { Edit, Delete, PictureAsPdf, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';

interface Delivery {
  id: string;
  doc_no: string;
  customer_name: string;
  work_title: string;
  delivery_date: string;
  issue_date: string;
  status: string;
  notes?: string;
}

const statusColors: Record<string, string> = {
  draft: '#9e9e9e',
  issued: '#1976d2',
  canceled: '#d32f2f',
};

export default function DeliveryList() {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string }>({ open: false, id: '' });

  useEffect(() => {
    loadDeliveries();
  }, [search]);

  const loadDeliveries = async () => {
    const result = await window.electronAPI.invoke('delivery:list', { search });
    setDeliveries(result);
  };

  const handleDelete = async () => {
    try {
      await window.electronAPI.invoke('delivery:delete', deleteDialog.id);
      setDeleteDialog({ open: false, id: '' });
      loadDeliveries();
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  const handlePrintPdf = async (id: string) => {
    try {
      const pdfPath = await window.electronAPI.invoke('delivery:printPdf', id);
      window.electronAPI.invoke('shell:openPath', pdfPath);
    } catch (error) {
      console.error('PDF出力エラー:', error);
    }
  };

  const columns = [
    { id: 'doc_no', label: '納品No', minWidth: 120 },
    { id: 'customer_name', label: '顧客名', minWidth: 150 },
    { id: 'work_title', label: '件名', minWidth: 200 },
    { id: 'delivery_date', label: '引渡日', minWidth: 100 },
    { id: 'issue_date', label: '発行日', minWidth: 100 },
    {
      id: 'status',
      label: 'ステータス',
      minWidth: 100,
      render: (value: string) => (
        <Chip
          label={value === 'draft' ? '下書き' : value === 'issued' ? '発行済' : 'キャンセル'}
          size="small"
          sx={{ backgroundColor: statusColors[value] || '#9e9e9e', color: 'white' }}
        />
      ),
    },
    {
      id: 'actions',
      label: '操作',
      minWidth: 150,
      render: (_: any, row: Delivery) => (
        <Box>
          <IconButton size="small" onClick={() => navigate(`/delivery/edit/${row.id}`)}>
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={() => handlePrintPdf(row.id)}>
            <PictureAsPdf />
          </IconButton>
          <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: row.id })}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">納品書一覧</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/delivery/create')}
            sx={{ mr: 1 }}
          >
            注文請書から作成
          </Button>
        </Box>
      </Box>

      <TextField
        label="検索"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />

      <DataTable columns={columns} rows={deliveries} />

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: '' })}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>この納品書を削除してもよろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: '' })}>キャンセル</Button>
          <Button onClick={handleDelete} color="error">削除</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
